import { useEffect, useState, useRef } from 'react';
import { toast } from 'sonner';
import { Loader2, Upload, ImagePlus, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { listMedia, uploadMedia, deleteMedia } from '@/lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const absolute = (rel) => (rel && rel.startsWith('http') ? rel : `${BACKEND_URL}${rel}`);

/**
 * Media Library modal. Lets admin pick an existing image OR upload a new one.
 * Props:
 *   - open / onOpenChange: controlled dialog state
 *   - onSelect(url, file): callback fired when user picks/uploads. Always receives ABSOLUTE url.
 *   - title?: optional dialog title (defaults to "Choose an image")
 */
export const MediaLibraryDialog = ({ open, onOpenChange, onSelect, title }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listMedia();
      setItems(res.data || []);
    } catch {
      toast.error('Could not load media library');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so same file can be picked again later
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large — max 10MB');
      return;
    }
    setUploading(true);
    try {
      const res = await uploadMedia(file);
      const url = absolute(res.data.url);
      toast.success('Image uploaded');
      load();
      onSelect?.(url, res.data);
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!confirm('Delete this image permanently?')) return;
    try {
      await deleteMedia(id);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success('Image deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  const pick = (item) => {
    onSelect?.(absolute(item.url), item);
    onOpenChange?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="media-library-dialog">
        <DialogHeader>
          <DialogTitle>{title || 'Media Library'}</DialogTitle>
          <DialogDescription>
            Pick from your uploads or upload a new image. Max 10MB. JPEG, PNG, GIF or WebP.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-3 pb-3 border-b">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleUpload}
            data-testid="media-upload-input"
          />
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="bg-sky-500 hover:bg-sky-600"
            data-testid="media-upload-btn"
          >
            {uploading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading…</>
            ) : (
              <><Upload className="h-4 w-4 mr-2" /> Upload new image</>
            )}
          </Button>
          <span className="text-xs text-slate-500">
            {items.length} image{items.length === 1 ? '' : 's'} in library
          </span>
        </div>

        <div className="flex-1 overflow-y-auto -mx-2 px-2 py-4">
          {loading ? (
            <div className="text-center py-12 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <ImagePlus className="h-10 w-10 mx-auto mb-3 text-slate-300" />
              No images yet. Click <strong>Upload new image</strong> above.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {items.map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => pick(it)}
                  className="group relative aspect-square overflow-hidden rounded-lg border border-slate-200 hover:border-sky-500 hover:shadow-md transition-all"
                  data-testid={`media-item-${it.id}`}
                  title={it.original_filename || it.name || it.id}
                >
                  <img
                    src={absolute(it.url)}
                    alt={it.original_filename || it.name || ''}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors pointer-events-none" />
                  <span
                    onClick={(e) => handleDelete(it.id, e)}
                    className="absolute top-1.5 right-1.5 p-1 rounded-full bg-white/90 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer hover:bg-red-50"
                    role="button"
                    aria-label="Delete image"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </span>
                  <div className="absolute bottom-0 left-0 right-0 px-1.5 py-1 bg-gradient-to-t from-black/70 to-transparent text-white text-[10px] truncate">
                    {it.original_filename || it.name || it.id}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MediaLibraryDialog;
